import { Capacitor } from '@capacitor/core';

/**
 * Subscription Configuration
 *
 * Product IDs must match:
 * - App Store Connect (iOS)
 * - Google Play Console (Android)
 * - Backend database mapping
 */

const platform = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'

interface SubscriptionProduct {
  id: string;
  planId: 'premium' | 'elite';
  type: 'paid subscription';
  title: string;
  description: string;
  price: string;
  billingPeriod: 'monthly' | 'yearly';
}

interface SubscriptionProducts {
  PREMIUM_MONTHLY: SubscriptionProduct;
  PREMIUM_YEARLY: SubscriptionProduct;
  ELITE_MONTHLY: SubscriptionProduct;
  ELITE_YEARLY: SubscriptionProduct;
}

/**
 * PLATFORM-SPECIFIC PRODUCT IDS
 */

const IOS_PRODUCT_IDS = {
  PREMIUM_MONTHLY: 'empathease_premium_monthly',
  PREMIUM_YEARLY: 'empathease_premium_yearly',
  ELITE_MONTHLY: 'empathease_elite_monthly_v2', // iOS uses _v2
  ELITE_YEARLY: 'empathease_elite_yearly',
};

const ANDROID_PRODUCT_IDS = {
  PREMIUM_MONTHLY: 'empathease_premium_monthly',
  PREMIUM_YEARLY: 'empathease_premium_yearly',
  ELITE_MONTHLY: 'empathease_elite_monthly', // Android NO _v2
  ELITE_YEARLY: 'empathease_elite_yearly',
};

const PRODUCT_IDS = platform === 'ios' ? IOS_PRODUCT_IDS : ANDROID_PRODUCT_IDS;

/**
 * FINAL PRODUCT CONFIG
 */

export const SUBSCRIPTION_PRODUCTS: SubscriptionProducts = {
  PREMIUM_MONTHLY: {
    id: PRODUCT_IDS.PREMIUM_MONTHLY,
    planId: 'premium',
    type: 'paid subscription',
    title: 'Premium Monthly',
    description: 'Full access to all NVC features',
    price: '$9.99/month',
    billingPeriod: 'monthly',
  },

  PREMIUM_YEARLY: {
    id: PRODUCT_IDS.PREMIUM_YEARLY,
    planId: 'premium',
    type: 'paid subscription',
    title: 'Premium Yearly',
    description: 'Full access with ~17% savings',
    price: '$99.99/year',
    billingPeriod: 'yearly',
  },

  ELITE_MONTHLY: {
    id: PRODUCT_IDS.ELITE_MONTHLY,
    planId: 'elite',
    type: 'paid subscription',
    title: 'Elite Monthly',
    description: 'Maximum features + priority support',
    price: '$99.99/month',
    billingPeriod: 'monthly',
  },

  ELITE_YEARLY: {
    id: PRODUCT_IDS.ELITE_YEARLY,
    planId: 'elite',
    type: 'paid subscription',
    title: 'Elite Yearly',
    description: 'Best value - save 17%',
    price: '$999.99/year',
    billingPeriod: 'yearly',
  },
};

/**
 * Runtime validation (dev only)
 */

const REQUIRED_PRODUCT_KEYS: (keyof SubscriptionProducts)[] = [
  'PREMIUM_MONTHLY',
  'PREMIUM_YEARLY',
  'ELITE_MONTHLY',
  'ELITE_YEARLY',
];

if (process.env.NODE_ENV === 'development') {
  REQUIRED_PRODUCT_KEYS.forEach((key: keyof SubscriptionProducts) => {
    if (!SUBSCRIPTION_PRODUCTS[key]) {
      throw new Error(`[subscriptions.config] Missing required product: ${key}`);
    } else if (!SUBSCRIPTION_PRODUCTS[key].id || !SUBSCRIPTION_PRODUCTS[key].planId) {
      throw new Error(
        `[subscriptions.config] Product ${key} is missing required fields (id, planId)`,
      );
    }
  });
}

// All product IDs for IAP initialization
export const ALL_PRODUCT_IDS: string[] = Object.values(SUBSCRIPTION_PRODUCTS).map(
  (p: SubscriptionProduct) => p.id,
);

// Get product by ID
export const getProductById = (productId: string): SubscriptionProduct | undefined => {
  return Object.values(SUBSCRIPTION_PRODUCTS).find((p: SubscriptionProduct) => p.id === productId);
};

export const getProductsByPlanId = (planId: 'premium' | 'elite') => {
  return Object.values(SUBSCRIPTION_PRODUCTS).filter((p) => p.planId === planId);
};

interface Feature {
  icon: string;
  title: string;
  description: string;
}

// Premium features (shown in marketing)
export const PREMIUM_FEATURES: Feature[] = [
  {
    icon: '✨',
    title: 'Advanced NVC Exercises',
    description: 'Access to premium practice scenarios',
  },
  {
    icon: '🎮',
    title: 'Interactive Games',
    description: 'Unlock all empathy-building games',
  },
  {
    icon: '🤖',
    title: 'AI Tutor',
    description: 'Personalized guidance from AI coach',
  },
  {
    icon: '📊',
    title: 'Advanced Analytics',
    description: 'Detailed progress tracking and insights',
  },
  {
    icon: '🎯',
    title: 'Custom Learning Paths',
    description: 'Tailored curriculum for your goals',
  },
  {
    icon: '👥',
    title: 'Community Access',
    description: 'Join our exclusive NVC community',
  },
  {
    icon: '📱',
    title: 'Offline Access',
    description: 'Download content for offline learning',
  },
  {
    icon: '🔔',
    title: 'Priority Support',
    description: 'Get help from our support team faster',
  },
];

// Elite-only features (in addition to Premium)
export const ELITE_FEATURES: Feature[] = [
  // NOTE: empathy sessions/circles are available on all tiers (gated by monthly
  // credits, see PricingSection) — they are NOT Elite-exclusive, so they don't
  // belong in this "Elite-only" list. Removed the old "Video Calls" entry for
  // that reason (the 1-on-1 sessions are live video on every tier, David
  // 2026-10-24; circles are audio). Add a genuine Elite-only perk here if one
  // exists.
  {
    icon: '🎯',
    title: '30,000 Monthly Credits',
    description: 'More credits for AI tools and features',
  },
];
